<template>
  <component
    :is="element"
    class="product-prices"
    :class="{ [$class('loading')]: loading }"
  >
    <span
      v-if="varies"
      class="product-prices__varies"
      :class="classPrice"
      v-text="$string('product.prices.from')"
    />

    <span
      v-if="formattedCompareAtPrice && !priceFirst"
      class="product-prices__compare-at-price"
      :class="[
        classCompareAtPrice,
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
    >
      <span
        class="visually-hidden"
        v-text="$string('cart.line_item.accessibility.compare_at_price')"
      />
      <span v-text="formattedCompareAtPrice" />
    </span>

    <span
      class="product-prices__price"
      :class="classPrice"
    >
      <span
        class="visually-hidden"
        v-text="$string('cart.line_item.accessibility.price')"
      />
      <span v-text="formattedPrice" />
    </span>

    <span
      v-if="formattedCompareAtPrice && priceFirst"
      class="product-prices__compare-at-price"
      :class="[
        classCompareAtPrice,
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
    >
      <span
        class="visually-hidden"
        v-text="$string('cart.line_item.accessibility.compare_at_price')"
      />
      <span v-text="formattedCompareAtPrice" />
    </span>

    <span
      v-if="formattedCompareAtPrice && finalSale"
      class="product-prices__final-sale"
      :class="finalSaleClass"
      v-text="$string('product.final_sale')"
    />

    <span
      v-if="available === false"
      class="product-prices__sold-out visually-hidden"
      :class="classPrice"
      v-text="$string('product.prices.sold_out')"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices (product-prices)
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} [available] - If the product is available.
 * @param {String} [classCompareAtPrice] - Compare at price text classes.
 * @param {String} [classPrice] - Price text classes.
 * @param {Number|Boolean} compareAtPrice - Compare at price, in cents format.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [priceFirst] - Display price before compare at price.
 * @param {Boolean} [finalSale] - Product contains final sale tag.
 * @param {Boolean} [varies] - Product variants vary in price.
 *
 */
export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    classCompareAtPrice: {
      type: String,
      default: 'text-body-2xs-regular',
    },
    classPrice: {
      type: String,
      default: 'text-body-2xs-regular',
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    priceFirst: {
      type: Boolean,
      default: false,
    },
    finalSale: {
      type: Boolean,
      default: false,
    },
    varies: {
      type: Boolean,
      default: false,
    },
    location: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice, cnvs?.store.moneyFormat, true)
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      return this.price ? this.$formatMoney(this.price, cnvs?.store.moneyFormat, true) : ''
    },

    /**
     * Get final sale message class
     */
    finalSaleClass() {
      let baseClass = this.classPrice

      if (this.containsCountryCode && this.location === 'product-card') {
        baseClass = `${this.classPrice} final-sale-small`
      }

      if (this.location && this.location === 'product-card') {
        return `${baseClass} font-family-freight-italic`
      }

      return baseClass
    },

    /**
     * Check if price contains country code
     * @param priceString
     */
    containsCountryCode() {
      const lettersInPrice = this.formattedCompareAtPrice.replace(/[^a-zA-Z]/g, '').length

      return lettersInPrice > 0
    },

  },
}
</script>
